// =============================================================================
// CALENDAR ARCHE TYPES
// =============================================================================

// Re-export API types with local aliases for backward compatibility
export type { CalendarCollectionDetail as CalendarCollection } from '@/lib/api';
export type { CalendarEventDetail as CalendarEvent } from '@/lib/api';
export type { CalendarEventCreateInput as CalendarEventInput } from '@/lib/api';
export type { CalendarEventInstance as EventInstance } from '@/lib/api';
export type { CalendarEventReminder as EventReminder } from '@/lib/api';
export type { CalendarParsedImportResult as ParsedCalendarImport } from '@/lib/api';
export type { CalendarImportedEvent as ImportedCalendarEvent } from '@/lib/api';
export type { CalendarImportConfirmRequest } from '@/lib/api';
export type { CalendarImportConfirmResult } from '@/lib/api';
export type { CalendarEventTemplateDetail as EventTemplate } from '@/lib/api';
export type { CalendarEventTemplateInput } from '@/lib/api';
export type { CalendarShareDetail } from '@/lib/api';
export type { CalendarShareInput } from '@/lib/api';
export type { CalendarShareUpdate } from '@/lib/api';
export type { CalendarSharePermission } from '@/lib/api';
export type { SharedCalendarSummary } from '@/lib/api';
export type { UserSearchResult } from '@/lib/api';
export type { FindTimeResult } from '@/lib/api';
export type { FindTimeInput } from '@/lib/api';
export type { FreeSlot } from '@/lib/api';
export type { BusyBlock } from '@/lib/api';
export type { ParticipantAvailability } from '@/lib/api';
export type { SchedulingLinkDetail as SchedulingLink } from '@/lib/api';
export type { SchedulingLinkInput } from '@/lib/api';
export type { BookingDetail as Booking } from '@/lib/api';
export type { AvailableSlotDetail as AvailableSlot } from '@/lib/api';
export type { AvailabilityWindow } from '@/lib/api';
export type { PublicSchedulingInfo } from '@/lib/api';
export type { BookingConfirmation } from '@/lib/api';
export type { SchedulableTaskDetail as SchedulableTask } from '@/lib/api';
export type { SchedulableTaskInput } from '@/lib/api';
export type { AiScheduleSuggestion } from '@/lib/api';
export type { FocusTimeSettingsDetail as FocusTimeSettings } from '@/lib/api';
export type { FocusTimeSettingsInput } from '@/lib/api';
export type { CalendarSubscriptionDetail } from '@/lib/api';

export type CalendarView = 'month' | 'week' | 'day' | 'agenda';

export interface WeatherDay {
  date: string;
  tempMax: number;
  tempMin: number;
  weatherCode: number;
}

export type RecurrenceEditScope = 'this' | 'this_and_future' | 'all';

export interface RecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  byDay?: string[];
  endType: 'never' | 'count' | 'until';
  count?: number;
  until?: string;
}

export interface CalendarSet {
  id: string;
  name: string;
  calendarIds: string[];
}

export interface DailyFocusPriority {
  date: string;
  priorities: string[];
}

export interface EveningReviewNote {
  date: string;
  reflection: string;
  completedCount: number;
  plannedCount: number;
}

export type TimeFormat = '12h' | '24h';

export interface AvailabilityWindowSetting {
  day: number;
  startHour: number;
  endHour: number;
}

export interface CalendarSettings {
  defaultView: CalendarView;
  weekStartDay: 0 | 1;
  workingHoursStart: number;
  workingHoursEnd: number;
  timeFormat: TimeFormat;
  timezone: string;
  defaultCalendarId: string | null;
  agendaDaysAhead: number;
  showWeekNumbers: boolean;
  sidebarCollapsed: boolean;
  calendarSets: CalendarSet[];
  activeCalendarSetId: string | null;
  showTaskPanel: boolean;
  lastPlanningDate: string | null;
  showWeather: boolean;
  // General — event defaults
  defaultEventDuration: number;
  defaultEventReminder: number | null;
  defaultEventStatus: 'confirmed' | 'tentative';
  defaultEventColor: string | null;
  dimPastEvents: boolean;
  showWeekends: boolean;
  // Display
  timeGridDensity: 'compact' | 'default' | 'comfortable';
  snapIntervalMinutes: number;
  monthViewMaxEvents: number;
  showEventTimesInMonthView: boolean;
  weekViewAnchor: 'fixed' | 'rolling';
  agendaGrouping: 'day' | 'calendar' | 'flat';
  eventColorPriority: 'event' | 'calendar';
  // Plan My Day
  dailyPlanningEnabled: boolean;
  /** Per-step toggles for the morning planning wizard */
  showReviewYesterday: boolean;
  showTodaysSchedule: boolean;
  showPlanYourDay: boolean;
  showSetYourFocus: boolean;
  morningWizardWindowStart: number;
  morningWizardWindowEnd: number;
  // Evening Review
  eveningReviewEnabled: boolean;
  eveningReviewWindowStart: number;
  eveningReviewWindowEnd: number;
  eveningReviewAutoTrigger: boolean;
  showEveningDaySummary: boolean;
  showEveningReschedule: boolean;
  showEveningReflection: boolean;
  lastEveningReviewDate: string | null;
  dailyFocusPriorities: DailyFocusPriority | null;
  eveningReviewNotes: EveningReviewNote[];
  // Tasks
  defaultTaskPriority: 'high' | 'medium' | 'low';
  defaultTaskDurationMinutes: number;
  autoScheduleNewTasks: boolean;
  taskSortOrder: 'priority' | 'deadline' | 'created';
  showCompletedTasks: 'never' | '24h' | '7d';
  defaultTaskDeadlineOffset: number | null;
  // Scheduling Links
  defaultSchedulingDurationMinutes: number;
  defaultSchedulingBufferMinutes: number;
  defaultSchedulingCalendarId: string | null;
  defaultAvailabilityWindows: AvailabilityWindowSetting[];
  defaultBookingConfirmationMessage: string;
  // Productivity
  maxMeetingsPerDay: number | null;
  minBreakBetweenEvents: number;
  showConflictWarnings: boolean;
  allowDoubleBooking: boolean;
  // Find a Time
  findTimeDefaultRangeDays: number;
  findTimeDefaultDurationMinutes: number;
  // Analytics
  analyticsDefaultPreset: '7d' | '30d' | '3mo';
  // Import
  importDefaultDuplicateStrategy: 'skip' | 'overwrite' | 'create_new';
  // Toolbar feature toggles — let users hide buttons for capabilities they don't use
  showToolbarTasks: boolean;
  showToolbarFocusTime: boolean;
  showToolbarSchedulingLinks: boolean;
  showToolbarFindTime: boolean;
  showToolbarAnalytics: boolean;
  showToolbarImport: boolean;
  // Fias AI panel
  aiPanelMode: 'popup' | 'inline' | 'off';
}

// =============================================================================
// CONSTANTS
// =============================================================================

export const DEFAULT_CALENDAR_SETTINGS: CalendarSettings = {
  defaultView: 'month',
  weekStartDay: 0, // Sunday
  workingHoursStart: 8,
  workingHoursEnd: 18,
  timeFormat: '12h',
  timezone: 'local',
  defaultCalendarId: null,
  agendaDaysAhead: 30,
  showWeekNumbers: false,
  sidebarCollapsed: false,
  calendarSets: [],
  activeCalendarSetId: null,
  showTaskPanel: false,
  lastPlanningDate: null,
  showWeather: true,
  // General — event defaults
  defaultEventDuration: 60,
  defaultEventReminder: null,
  defaultEventStatus: 'confirmed',
  defaultEventColor: null,
  dimPastEvents: true,
  showWeekends: true,
  // Display
  timeGridDensity: 'default',
  snapIntervalMinutes: 15,
  monthViewMaxEvents: 3,
  showEventTimesInMonthView: false,
  weekViewAnchor: 'fixed',
  agendaGrouping: 'day',
  eventColorPriority: 'event',
  // Plan My Day
  dailyPlanningEnabled: true,
  showReviewYesterday: true,
  showTodaysSchedule: true,
  showPlanYourDay: true,
  showSetYourFocus: true,
  morningWizardWindowStart: 6,
  morningWizardWindowEnd: 11,
  // Evening Review
  eveningReviewEnabled: true,
  eveningReviewWindowStart: 16,
  eveningReviewWindowEnd: 22,
  eveningReviewAutoTrigger: false,
  showEveningDaySummary: true,
  showEveningReschedule: true,
  showEveningReflection: true,
  lastEveningReviewDate: null,
  dailyFocusPriorities: null,
  eveningReviewNotes: [],
  // Tasks
  defaultTaskPriority: 'medium',
  defaultTaskDurationMinutes: 60,
  autoScheduleNewTasks: false,
  taskSortOrder: 'priority',
  showCompletedTasks: 'never',
  defaultTaskDeadlineOffset: null,
  // Scheduling Links
  defaultSchedulingDurationMinutes: 30,
  defaultSchedulingBufferMinutes: 0,
  defaultSchedulingCalendarId: null,
  defaultAvailabilityWindows: [
    { day: 1, startHour: 9, endHour: 17 },
    { day: 2, startHour: 9, endHour: 17 },
    { day: 3, startHour: 9, endHour: 17 },
    { day: 4, startHour: 9, endHour: 17 },
    { day: 5, startHour: 9, endHour: 17 },
  ],
  defaultBookingConfirmationMessage: '',
  // Productivity
  maxMeetingsPerDay: null,
  minBreakBetweenEvents: 0,
  showConflictWarnings: true,
  allowDoubleBooking: true,
  // Find a Time
  findTimeDefaultRangeDays: 7,
  findTimeDefaultDurationMinutes: 60,
  // Analytics
  analyticsDefaultPreset: '30d',
  // Import
  importDefaultDuplicateStrategy: 'skip',
  // Toolbar feature toggles
  showToolbarTasks: false,
  showToolbarFocusTime: false,
  showToolbarSchedulingLinks: false,
  showToolbarFindTime: false,
  showToolbarAnalytics: false,
  showToolbarImport: false,
  // Fias AI panel
  aiPanelMode: 'popup',
};

export const PRESET_COLORS = [
  '#3b82f6', // blue
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#6b7280', // gray
  '#78716c', // stone
];

export const HOUR_HEIGHT_PX = 60;

export function getHourHeight(density: 'compact' | 'default' | 'comfortable'): number {
  switch (density) {
    case 'compact':
      return 40;
    case 'default':
      return 60;
    case 'comfortable':
      return 80;
  }
}

export const REMINDER_PRESETS = [
  { label: 'At time of event', minutes: 0 },
  { label: '5 minutes before', minutes: 5 },
  { label: '15 minutes before', minutes: 15 },
  { label: '30 minutes before', minutes: 30 },
  { label: '1 hour before', minutes: 60 },
  { label: '2 hours before', minutes: 120 },
  { label: '1 day before', minutes: 1440 },
  { label: '1 week before', minutes: 10080 },
];

export const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Day',
  weekly: 'Week',
  monthly: 'Month',
  yearly: 'Year',
};

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DAY_NAMES_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
